/**
 * A catalog model's definition, applied by the editor when the model loads — as the app does it.
 *
 * The web worker converts only the geometry (format 2, `api/model.py`): the mesh stays in the
 * FBX's own frame, and the archive's pictures travel under their file names. What the definition
 * says is read here: the per-axis <Scale> and the <Rotation> turn the mesh, <Position> places its
 * pivot, the first variation's slots dress its materials, and every later <Variations> group dresses
 * each variation. A new definition then needs no new conversion, only a new look at this file.
 */
import { attr, kids, parseXml, type Node } from '../../core/hp/xml';
import { t } from '../../i18n/I18n';

type Vec3 = [number, number, number];

export interface DefinitionSlot { for: string; texture?: string; color?: string; tiling?: [number, number] }

export interface ModelDefinition {
  /** Width, Height, Depth in centimetres. */
  sizeCm: Vec3;
  positionCm: Vec3;
  scale: Vec3;
  /** <Rotation> y in degrees, as the app reads it. */
  rotationY: number;
  variations: Array<{ id: string; slots: DefinitionSlot[] }>;
  tags: string[];
  /** The FBX the definition names (`Model="Model/1498.fbx"`). */
  model: string;
}

/** The fields the definition falls back to on the catalog document. */
export interface DefinitionFallback { width?: number; height?: number; depth?: number; tags?: string[] }

function number(node: Node | undefined, name: string, fallback: number): number {
  const value = Number(attr(node, name));
  return attr(node, name) !== undefined && Number.isFinite(value) ? value : fallback;
}

function vector(node: Node | undefined, fallback: number): Vec3 {
  return [number(node, 'x', number(node, 'X', fallback)), number(node, 'y', number(node, 'Y', fallback)),
    number(node, 'z', number(node, 'Z', fallback))];
}

function slotOf(node: Node, forName: string): DefinitionSlot {
  const slot: DefinitionSlot = { for: forName };
  // Cloud definitions say `Source`; catalog XMLs say `Texture`.
  const texture = attr(node, 'Source') ?? attr(node, 'Texture');
  if (texture) slot.texture = texture;
  const color = attr(node, 'Color');
  if (color) slot.color = color;
  const tiling = kids(node, 'Tiling')[0];
  if (tiling) {
    const tx = number(tiling, 'x', 1) || 1, ty = number(tiling, 'y', 1) || 1;
    if (Math.abs(tx - 1) > 1e-6 || Math.abs(ty - 1) > 1e-6) slot.tiling = [tx, ty];
  }
  return slot;
}

function itemSlots(item: Node, groupFor: string | undefined): DefinitionSlot[] {
  const nested = kids(item, 'Item');
  // A `For` lists its parts by commas, as the app's VariationMaterialInfo.Load splits it.
  return (nested.length ? nested : [item]).flatMap((part) => (attr(part, 'For') ?? groupFor ?? '').split(',').map((name) => slotOf(part, name)))
    .filter((slot) => slot.for || slot.texture || slot.color);
}

/** The worker's `cloud_definition` reading, and the later <Variations> groups the format-1 worker skips. */
export function parseModelDefinition(xml: string, fallback: DefinitionFallback = {}): ModelDefinition {
  const parsed = parseXml(xml.replace(/^﻿/, ''));
  const found = 'Item' in parsed ? parsed.Item : Object.values(parsed).find((value): value is Node => !!value && typeof value === 'object' && 'Item' in value)?.Item;
  if (found === undefined) throw new Error(t('#ViewModelDefinitionNoItem'));
  // An empty <Item/> parses to an empty string.
  const root: Node = found && typeof found === 'object' ? found : {};
  const [main, ...later] = kids(root, 'Variations');
  // As the app's FurnitureInfo.LoadDefaultMaterials + FurnitureVariation.LoadMaterials(dontOverride); its random item is the only one in every app group.
  const common = later.flatMap((group) => kids(group, 'Item').slice(0, 1).flatMap((item) => itemSlots(item, attr(group, 'For'))));
  const listed = kids(main, 'Item').map((variation) => ({ id: attr(variation, 'Id') ?? '', slots: itemSlots(variation, attr(main, 'For')) }));
  const variations = (listed.length || !common.length ? listed : [{ id: '', slots: [] }]).map((variation) => ({ ...variation,
    slots: common.reduce((slots, slot) => slots.some((own) => normalizeKey(own.for) === normalizeKey(slot.for)) ? slots : [...slots, slot], variation.slots) }));
  const tags = attr(root, 'Tags') ?? (fallback.tags ?? []).join(',');
  return {
    sizeCm: [number(root, 'Width', fallback.width ?? 0), number(root, 'Height', fallback.height ?? 0), number(root, 'Depth', fallback.depth ?? 0)],
    positionCm: vector(kids(root, 'Position')[0], 0),
    scale: vector(kids(root, 'Scale')[0], 1).map((v) => Math.abs(v) || 1) as Vec3,
    rotationY: number(kids(root, 'Rotation')[0], 'y', number(kids(root, 'Rotation')[0], 'Y', 0)),
    variations,
    tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    model: attr(root, 'Model') ?? '',
  };
}

/**
 * The app's own models a 2D item borrows (FurnitureInfo.DoesBorrow2dModel,
 * BuiltinAssetStorage.Get2dAssetFolder): a 2D item has no geometry of its own, it is one of these
 * quads stretched to its Width, Height and Depth, its picture on the quad's picture material.
 */
export const BORROWED_2D: Record<string, { id: string; material: string }> = {
  '029.fbx': { id: 'carpets.ikea/029/029', material: 'glass' },
  '082.fbx': { id: 'decor.paintings/082/082', material: '2' },
  '1497.fbx': { id: 'decor/1497/1497', material: 'material' },
  '1498.fbx': { id: 'decor/1498/1498', material: 'sticker' },
};

/** The quad a 2D item borrows, when the definition names one. */
export function borrowedQuad(definition: ModelDefinition): { id: string; material: string } | undefined {
  return BORROWED_2D[(definition.model.replace(/\\/g, '/').split('/').pop() ?? '').toLowerCase()];
}

/**
 * A 2D item's definition fitted onto its borrowed quad, as the app does it: the definition's own
 * <Scale> is ignored, and the quad is stretched until it measures Width × Height × Depth once
 * turned. An axis the quad has no thickness along keeps it.
 */
export function fittedToQuad(definition: ModelDefinition, quad: { min: Vec3; max: Vec3 }): ModelDefinition {
  const quarter = Math.round(definition.rotationY / 90) % 2 !== 0;
  const wanted = definition.sizeCm.map((v) => v / 100);
  const along: Vec3 = quarter ? [wanted[2], wanted[1], wanted[0]] : [wanted[0], wanted[1], wanted[2]];
  const scale = [0, 1, 2].map((i) => {
    const extent = quad.max[i] - quad.min[i];
    return extent > 1e-4 && along[i] > 0 ? along[i] / extent : 1;
  }) as Vec3;
  return { ...definition, scale };
}

/** The turn the app gives the model, about Y, in three.js radians. */
export function yawOf(definition: ModelDefinition): number { return -definition.rotationY * Math.PI / 180; }

/** A point of the mesh, scaled and turned as the definition says. */
export function placePoint(definition: ModelDefinition, p: Vec3): Vec3 {
  const yaw = yawOf(definition), cy = Math.cos(yaw), sy = Math.sin(yaw);
  const [s0, s1, s2] = [p[0] * definition.scale[0], p[1] * definition.scale[1], p[2] * definition.scale[2]];
  return [s0 * cy + s2 * sy, s1, -s0 * sy + s2 * cy];
}

/**
 * The placed mesh's measures, from its raw bounds: the numbers the worker used to write — real
 * size, bounds, and the pivot the app puts at <Position>.
 */
export function placedMeasures(definition: ModelDefinition, geometry: { min: Vec3; max: Vec3 }):
  { size: Vec3; bounds: { min: Vec3; max: Vec3 }; pivot: Vec3 } {
  const low: Vec3 = [Infinity, Infinity, Infinity], high: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const x of [geometry.min[0], geometry.max[0]]) for (const y of [geometry.min[1], geometry.max[1]]) for (const z of [geometry.min[2], geometry.max[2]]) {
    const p = placePoint(definition, [x, y, z]);
    for (let i = 0; i < 3; i++) { low[i] = Math.min(low[i], p[i]); high[i] = Math.max(high[i], p[i]); }
  }
  const round = (v: number) => Math.round(v * 1e6) / 1e6;
  // A flat mesh measures zero across its thickness, which makes the parser drop the item.
  const size = [0, 1, 2].map((i) => high[i] - low[i] > 1e-4 ? round(high[i] - low[i]) : Math.max(definition.sizeCm[i] / 100, 1e-3)) as Vec3;
  const [px, py, pz] = definition.positionCm;
  return {
    size,
    bounds: { min: low.map(round) as Vec3, max: high.map(round) as Vec3 },
    pivot: [round(px / 100), round(low[1] + py / 100), round(pz / 100)],
  };
}

/** The slot a material takes its stock look from: the first one naming it, in any variation. */
export function slotFor(definition: ModelDefinition, material: string): DefinitionSlot | undefined {
  const key = normalizeKey(material);
  for (const variation of definition.variations) for (const slot of variation.slots) if (normalizeKey(slot.for) === key) return slot;
  return undefined;
}

export function normalizeKey(name: string): string {
  return name.replace(/\s*\(Instance\)\s*$/i, '').trim().toLowerCase();
}

/** An archive picture by the slot's name: exactly, then ignoring case, then by stem (`2.jpg` finds `2.jpeg`). */
export function pickImage(images: Record<string, number>, wanted: string): number | undefined {
  const base = wanted.replace(/\\/g, '/').split('/').pop() ?? '';
  if (base in images) return images[base];
  const lower = base.toLowerCase();
  for (const [name, index] of Object.entries(images)) if (name.toLowerCase() === lower) return index;
  const stem = lower.replace(/\.[^.]+$/, '');
  for (const [name, index] of Object.entries(images)) if (name.toLowerCase().replace(/\.[^.]+$/, '') === stem) return index;
  return undefined;
}
